import { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { SHORT_DATE_FORMAT } from "shared/constants";
import { formatDate } from "shared/locale/format";

import Image from "./Image";
import ImageButtons from "./ImageButtons";
import ImagesModal from "./ImagesModal";
import ThumbnailList from "./ThumbnailList";
import { GalleryPhoto } from "./types";
import { isVideoMimeType } from "./utils";
import Video from "./Video";

interface ImagesProps {
  images?: GalleryPhoto[];
  title?: string | null;
  className?: string;
  imageHeader?: JSX.Element;
  noImagesClassName?: string;
  disableInlineViewer?: boolean;
  hideImageNavigation?: boolean;
  // Fill the parent's width (no max-width cap) and derive the viewer height from
  // it, instead of the default fixed 480x320 gallery. Opt-in so v1 Claims keeps
  // its sizing; used by the PageBuilder attachment image nodes.
  fitContainer?: boolean;
}

export const Images = ({
  images,
  title,
  className,
  imageHeader,
  noImagesClassName,
  disableInlineViewer = false,
  hideImageNavigation = false,
  fitContainer = false,
}: ImagesProps) => {
  const { t } = useTranslation("common");
  const [selectedImageId, setSelectedImageId] = useState<string | undefined>(
    images?.[0]?.ID
  );

  const [modalOpen, setModalOpen] = useState(false);

  // Callers rebuild the array every render, so track the ids instead: resetting
  // on array identity would snap the reader back to the first image on any
  // re-render (an upload in progress ticks several per second).
  const imageIds = images?.map(({ ID }) => ID).join() ?? "";
  const [shownImageIds, setShownImageIds] = useState(imageIds);
  if (imageIds !== shownImageIds) {
    setShownImageIds(imageIds);
    setSelectedImageId(images?.[0]?.ID);
  }

  if (!images || !images[0]) {
    return (
      <div
        className={classNames(
          "bg-gray-50 text-sm w-full h-80 rounded-sm border border-gray-200 border-dashed p-2 flex flex-col justify-center items-center",
          noImagesClassName
        )}
      >
        [{t("images.none")}]
      </div>
    );
  }

  const selectedImage = images.find(({ ID }) => ID === selectedImageId);
  const thumbnails = hideImageNavigation
    ? []
    : images
        .filter((t) => t.srcThumbnail)
        .map(({ ID, srcThumbnail }) => ({
          url: srcThumbnail!,
          ID,
        }));

  const onThumbnailClick = (id: string) => {
    setSelectedImageId(id);
    if (disableInlineViewer) {
      setModalOpen(true);
    }
  };

  return (
    <div
      className={classNames(
        "w-full",
        fitContainer ? "flex h-full flex-col" : "max-w-120"
      )}
    >
      {!disableInlineViewer && (
        <div
          className={classNames(
            "w-full bg-slate-100 rounded-sm relative cursor-zoom-in overflow-hidden",
            // The aspect ratio sets the viewer's natural height from its width;
            // flex lets it stretch further when a taller row sibling gives the
            // card extra height, so image nodes in a row stay even.
            fitContainer ? "aspect-[3/2] min-h-0 flex-1" : "h-80",
            className
          )}
          onClick={() => setModalOpen(true)}
        >
          {selectedImage && !isVideoMimeType(selectedImage) && (
            <Image
              photoSrc={selectedImage.srcUrl ?? ""}
              photoId={selectedImage.ID}
              className="rounded-sm"
            />
          )}
          {selectedImage &&
            isVideoMimeType(selectedImage) &&
            selectedImage.srcUrl &&
            selectedImage.srcExpire && (
              <Video
                photoID={selectedImage.ID}
                className="rounded-sm"
                srcExpire={selectedImage.srcExpire}
                srcUrl={selectedImage.srcUrl}
              />
            )}

          {!hideImageNavigation && (
            <div className="absolute z-10 bottom-2 right-2">
              <ImageButtons
                images={images}
                selectedImageId={selectedImageId}
                setSelectedImageId={setSelectedImageId}
              />
            </div>
          )}
          {selectedImage?.uploadedAt && (
            <div className="absolute z-10 bottom-2 left-2 px-2 py-1 bg-gray-50/50 rounded-md">
              {formatDate(selectedImage.uploadedAt, SHORT_DATE_FORMAT)}
            </div>
          )}
        </div>
      )}

      {!hideImageNavigation && (
        <ThumbnailList
          thumbnails={thumbnails}
          selectedId={selectedImageId}
          onClick={onThumbnailClick}
          onShowAll={
            !disableInlineViewer ? undefined : () => setModalOpen(true)
          }
        />
      )}
      {modalOpen && (
        <ImagesModal
          initialSelectedImageId={selectedImageId}
          title={title}
          images={images}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          header={imageHeader}
          hideImageNavigation={hideImageNavigation}
        />
      )}
    </div>
  );
};
